import { useEffect, useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import Countdown from "@/components/Countdown";
import { Sparkles, Users, HeartHandshake, Compass, Calendar, MapPin, Clock, ChevronRight, Instagram, MessageCircle, ArrowRight } from "lucide-react";

const pillars = [
  { icon: Sparkles, name: "FÉ", desc: "Relacionamento genuíno com Deus, sem religiosidade fria." },
  { icon: Users, name: "COMUNHÃO", desc: "Mesa aberta, risadas e conexões reais que duram." },
  { icon: HeartHandshake, name: "AMIZADE", desc: "Um lugar seguro para ser você mesmo e crescer junto." },
  { icon: Compass, name: "PROPÓSITO", desc: "Despertando a juventude para impactar sua geração." },
];

export default function Home() {
  const { settings } = useOutletContext();
  const [next, setNext] = useState(null);
  const [encontros, setEncontros] = useState([]);
  const [devs, setDevs] = useState([]);
  const [anns, setAnns] = useState([]);
  const [gallery, setGallery] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/events/next").then((r) => setNext(r.data?.id ? r.data : null)).catch(() => {});
    api.get("/events/public").then((r) => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const future = r.data
        .filter((e) => (e.event_type || "edicao") === "encontro" && e.status !== "past" && new Date(e.start_date) >= today)
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setEncontros(future);
    }).catch(() => {});
    api.get("/devotionals/public").then((r) => setDevs(r.data.slice(0, 3))).catch(() => {});
    api.get("/announcements/public").then((r) => setAnns(r.data.slice(0, 3))).catch(() => {});
    api.get("/gallery/public").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const dateStr = next?.start_date ? new Date(next.start_date).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
  const countdownTarget = next ? `${next.start_date}T${(next.time || "19:30").padEnd(5, "0")}:00` : null;
  const showNextEdicao = next && (next.event_type || "edicao") === "edicao";

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[94vh] flex items-center ambient-grid">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] max-w-full rounded-full opacity-70 blur-3xl"
               style={{ background: "radial-gradient(circle, rgba(30,144,255,0.32) 0%, rgba(24,119,242,0.1) 45%, transparent 70%)" }} />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#000000] to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full text-center">
          <div className="relative inline-block animate-fade-up">
            <div className="absolute -inset-16 -z-10 rounded-full blur-3xl animate-brand-glow"
                 style={{ background: "radial-gradient(circle, rgba(30,144,255,0.55) 0%, rgba(30,144,255,0.15) 45%, transparent 70%)" }} />
            <Logo size="xl" />
          </div>
          <div className="label mt-6 sm:mt-8 animate-fade-up" style={{ animationDelay: "100ms" }}>UM MOVIMENTO JOVEM • FÉ · COMUNHÃO · PROPÓSITO</div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mt-5 max-w-4xl mx-auto animate-fade-up" style={{ animationDelay: "180ms" }}>
            Porque há momentos que não conseguimos{" "}
            <span className="brand-blue text-glow-blue">explicar</span>, apenas viver.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: "260ms" }}>
            Um lugar para conectar pessoas, viver experiências, fortalecer amizades e estar na presença de Deus.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center animate-fade-up" style={{ animationDelay: "340ms" }}>
            <button data-testid="hero-rsvp-btn" onClick={() => nav("/confirmar-presenca")} className="btn-primary">
              CONFIRMAR PRESENÇA <ArrowRight size={18} />
            </button>
            <button data-testid="hero-next-btn" onClick={() => nav("/proximo-encontro")} className="btn-ghost">
              PRÓXIMO ENCONTRO
            </button>
          </div>
          <div className="mt-12 flex gap-4 justify-center text-slate-400 text-sm">
            {settings?.instagram_url && (
              <a data-testid="hero-ig-link" href={settings.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-blue-400">
                <Instagram size={16} /> {settings?.instagram_handle || "@conexao.inefavel"}
              </a>
            )}
            {settings?.whatsapp_number && (
              <a data-testid="hero-wa-link" href={`https://wa.me/${settings.whatsapp_number.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-blue-400">
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* NEXT EDITION (edicao only) */}
      {showNextEdicao && (
        <section className="relative py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-10">
              <div>
                <div className="label">Próxima Edição</div>
                <h2 className="font-display text-3xl sm:text-5xl font-extrabold mt-2">
                  {next.edition_number}ª Edição
                </h2>
              </div>
              <span className={`text-xs font-bold tracking-widest px-3 py-1.5 rounded-full ${next.registrations_open ? "bg-blue-500/15 text-blue-300 border border-blue-500/40" : "bg-slate-800 text-slate-400 border border-slate-700"}`}>
                {next.registrations_open ? "INSCRIÇÕES ABERTAS" : "INSCRIÇÕES ENCERRADAS"}
              </span>
            </div>

            <div className="ci-card p-6 sm:p-10 grid gap-8 lg:grid-cols-2 items-center">
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold">{next.name}</h3>
                <p className="text-slate-300 mt-3 leading-relaxed">{next.description}</p>
                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-slate-300"><Calendar size={16} className="text-blue-400" /> {dateStr}{next.end_date && ` → ${new Date(next.end_date).toLocaleDateString("pt-BR")}`}</div>
                  {next.time && <div className="flex items-center gap-3 text-slate-300"><Clock size={16} className="text-blue-400" /> {next.time}</div>}
                  {next.location && <div className="flex items-center gap-3 text-slate-300"><MapPin size={16} className="text-blue-400" /> {next.location}</div>}
                </div>
                <div className="mt-8">
                  <button data-testid="next-rsvp-btn" onClick={() => nav("/confirmar-presenca")} disabled={!next.registrations_open} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                    GARANTIR MINHA VAGA <ArrowRight size={18} />
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="label text-center">Contagem Regressiva</div>
                <Countdown target={countdownTarget} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* UPCOMING ENCONTROS (list, cronological) */}
      {encontros.length > 0 && (
        <section data-testid="encontros-section" className="relative py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10">
              <div className="label">Agenda</div>
              <h2 data-testid="encontros-title" className="font-display text-3xl sm:text-5xl font-extrabold mt-2">
                {encontros.length === 1 ? "Próximo Encontro" : "Próximos Encontros"}
              </h2>
            </div>
            <div className={`grid gap-5 ${encontros.length === 1 ? "" : "md:grid-cols-2"}`}>
              {encontros.map((e) => {
                const target = `${e.start_date}T${(e.time || "19:30").padEnd(5, "0")}:00`;
                const d = new Date(e.start_date).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
                return (
                  <article key={e.id} data-testid={`encontro-card-${e.id}`} className="ci-card p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="label">Encontro</div>
                        <h3 className="font-display text-xl sm:text-2xl font-bold mt-2">{e.name}</h3>
                      </div>
                      <span className="text-[10px] font-bold tracking-widest px-2 py-1 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/40 shrink-0">ENCONTRO</span>
                    </div>
                    {e.description && <p className="text-slate-300 mt-3 text-sm leading-relaxed">{e.description}</p>}
                    <div className="mt-5 space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-300"><Calendar size={14} className="text-blue-400" /> {d}</div>
                      {e.time && <div className="flex items-center gap-2 text-slate-300"><Clock size={14} className="text-blue-400" /> {e.time}</div>}
                      {e.location && <div className="flex items-center gap-2 text-slate-300"><MapPin size={14} className="text-blue-400" /> {e.location}</div>}
                    </div>
                    <div className="mt-6 pt-5 border-t border-white/5">
                      <div className="label text-center mb-3">Contagem Regressiva</div>
                      <Countdown target={target} />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 4 PILLARS */}
      <section className="relative py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label">Sobre o Inefável</div>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold mt-3">
              Mais do que um encontro. <span className="text-blue-400">Uma conexão.</span>
            </h2>
            <p className="text-slate-300 mt-4">
              {settings?.about_text}
            </p>
          </div>
          <div className="grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-4 mt-12">
            {pillars.map(({ icon: Icon, name, desc }, i) => (
              <div key={name} data-testid={`pillar-${name.toLowerCase()}`} className="ci-card p-6 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <Icon size={28} className="text-blue-400" />
                <h3 className="mt-4 font-display text-xl font-bold">{name}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEVOTIONALS PREVIEW */}
      {devs.length > 0 && (
        <section className="relative py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
              <div>
                <div className="label">Palavra</div>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Momentos com Deus.</h2>
              </div>
              <Link to="/palavra" className="text-sm text-blue-400 inline-flex items-center gap-1 hover:gap-2 transition-all">Ver todos <ChevronRight size={14} /></Link>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {devs.map((d) => (
                <article key={d.id} className="ci-card p-6">
                  <div className="label">{d.reference}</div>
                  <blockquote className="mt-3 font-display text-lg leading-snug text-white">"{d.verse}"</blockquote>
                  <p className="mt-3 text-sm text-slate-400 line-clamp-3">{d.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ANNOUNCEMENTS */}
      {anns.length > 0 && (
        <section className="relative py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="label">Novidades</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 mb-10">Fique por dentro.</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {anns.map((a) => (
                <article key={a.id} className="ci-card p-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("pt-BR")}</span>
                    {a.priority === "important" && <span className="text-[10px] font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 px-2 py-0.5 rounded-full">IMPORTANTE</span>}
                  </div>
                  <h3 className="font-display text-lg font-bold mt-3">{a.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-3">{a.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALLERY TEASER */}
      {gallery.length > 0 && (
        <section className="relative py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
              <div>
                <div className="label">Memórias</div>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Momentos que ficam.</h2>
              </div>
              <Link to="/memorias" className="text-sm text-blue-400 inline-flex items-center gap-1 hover:gap-2 transition-all">Ver galeria <ChevronRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="aspect-square rounded-xl overflow-hidden ci-card p-0">
                  <img src={fileUrl(g.storage_path)} alt={g.caption} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
